import { useEffect, useState } from "react";
import Modal from "../../../elements/Modal";
import { fetchInsightsByIterationId } from "../../../../modules/executions";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Cell,
} from "recharts";
import { Chip, Typography } from "@mui/material";

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const { name, count, color } = payload[0].payload;

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #ddd",
                padding: "8px 12px",
                borderRadius: 6,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
        >
            <div
                style={{
                    color,
                    marginBottom: 2,
                }}
            >
                {name}
            </div>
            <div style={{ fontSize: 13 }}>
                Executions: <strong>{count}</strong>
            </div>
            <div style={{ fontSize: 12, color: "#666" }}>
                {payload[0].payload.percentage}%
            </div>
        </div>
    );
};


const IterationInsights = ({ showModal, handleCloseModal, iterationId }) => {
    const [insights, setInsights] = useState(null);

    useEffect(() => {
        if (!showModal) return;

        const fetchInsights = async () => {
            try {
                const response = await fetchInsightsByIterationId(iterationId);
                setInsights(response);
            } catch (error) {
                console.error(error);
            }
        };

        fetchInsights();
    }, [showModal, iterationId]);

    if (!insights) return null;

    const { total_executions, distribution, is_closed, iteration } = insights;

    const sortedDistribution = [...distribution].sort(
        (a, b) => b.count - a.count
    );

    return (
        <Modal
            open={showModal}
            handleClose={handleCloseModal}
            width="md"
            modalTitle="Insights">
            <div>
                <Typography
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {iteration?.name}
                    <Chip
                        label={is_closed ? "Closed" : "Open"}
                        size="small"
                        sx={{
                            fontWeight: 500,
                            backgroundColor: is_closed ? "#fdecea" : "#edf7ed",
                            color: is_closed ? "#c62828" : "#2e7d32",
                        }}
                    />
                </Typography>
                <Typography variant="body2" color="textSecondary">{iteration.environment} {iteration.country.length > 0 && " | " + iteration.country.join(", ")}</Typography>

            </div>
            <div
                style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    marginTop: 20,
                }}
            >
                <div style={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600}}>
                        Total Executions: {total_executions}
                    </Typography>

                    {sortedDistribution.map((status) => (
                        <div
                            key={status.statusId}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                            }}
                        >
                            <span
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    backgroundColor: status.color,
                                    flexShrink: 0,
                                }}
                            />

                            <Typography sx={{ minWidth: 110 }}>
                                {status.name}
                            </Typography>

                            <Typography>
                                {status.count}
                            </Typography>
                        </div>
                    ))}
                </div>

                <div style={{ height: 300, width: 500 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={sortedDistribution}
                            margin={{ top: 10, right: 20, left: 0, bottom: 30 }}
                        >
                            <CartesianGrid
                                stroke="#eee"
                                vertical={false}
                                strokeDasharray="2 2"
                            />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 12 }}
                                interval={0}
                            />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="count" barSize={36} radius={[4, 4, 0, 0]}>
                                {sortedDistribution.map((s) => (
                                    <Cell key={s.statusId} fill={s.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Modal>
    );
};

export default IterationInsights;
